import { action, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { asNumber } from '@/utils/number-util'
import type { DeadlineReminderConfig } from './legend-options'

/**
 * Reminds users that the request deadline is coming up.
 */
export class DeadlineRemindersConfigEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<DeadlineRemindersConfigEditorViewModel> =
    {
      daysBefore: [
        func(
          ({ value }) => asNumber(value) > 0 && asNumber(value) <= 21,
          'Days Before must be between 1 and 21.',
        ),
      ],
    }

  @observable accessor enabled = true
  @observable accessor daysBefore = 7

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * Turns the reminders on or off.
   */
  @action.bound
  setEnabled(value: boolean) {
    this.enabled = value
  }

  /**
   * Reminders are off without a reminder config, and on when it doesn't say.
   */
  @action
  hydrate(config: DeadlineReminderConfig | undefined) {
    if (!config) {
      this.enabled = false
      this.daysBefore = 7
      return
    }
    this.enabled = config.enabled ?? true
    // A stored 0 falls back like a missing value: keeping it would fail
    // validation and block publishing.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    this.daysBefore = config.daysBefore || 7
  }

  /**
   * Shows the analyzer's errors on the reminders.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The reminders as the legend document has them.
   */
  toJSON(): DeadlineReminderConfig {
    return { enabled: this.enabled, daysBefore: asNumber(this.daysBefore) }
  }
}
